import { useCallback, useEffect, useState } from "react";
import { adminEvents, adminCreateEvent, adminUpdateEvent, adminDeleteEvent, adminEventRsvps } from "@/lib/api";
import { Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import ResourceList from "@/components/admin/ResourceList";
import ResourceDrawer from "@/components/admin/ResourceDrawer";

const EMPTY = {
  slug: "", title: "", description: "", location: "",
  starts_at: "", ends_at: "", capacity: 0, status: "Open",
};

function EventRow({ e, onEdit, onDelete, onRsvps }) {
  return (
    <div className="bg-white border border-stone-200 p-5 flex items-start justify-between">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-ccc-gold-dark">
          {new Date(e.starts_at).toLocaleDateString()}
        </div>
        <div className="font-serif text-xl text-ccc-plum">{e.title}</div>
        <div className="text-sm text-stone-600">{e.location}</div>
      </div>
      <div className="flex gap-2 shrink-0 items-center">
        <button onClick={() => onRsvps(e)} className="text-ccc-purple text-sm inline-flex items-center gap-1"><Users size={14}/> RSVPs</button>
        <button onClick={() => onEdit(e)} className="text-ccc-purple text-sm underline">Edit</button>
        <button onClick={() => onDelete(e.id)} className="text-red-600" aria-label="Delete"><Trash2 size={14}/></button>
      </div>
    </div>
  );
}

function EventForm({ editing, setEditing }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <input placeholder="Slug" value={editing.slug} onChange={e => setEditing({...editing, slug: e.target.value})} className="px-3 py-2 border border-stone-300"/>
      <select value={editing.status} onChange={e => setEditing({...editing, status: e.target.value})} className="px-3 py-2 border border-stone-300">
        <option>Open</option><option>Closed</option><option>Full</option>
      </select>
      <input placeholder="Title" value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300"/>
      <input placeholder="Location" value={editing.location} onChange={e => setEditing({...editing, location: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300"/>
      <input type="datetime-local" value={editing.starts_at} onChange={e => setEditing({...editing, starts_at: e.target.value})} className="px-3 py-2 border border-stone-300"/>
      <input type="datetime-local" value={editing.ends_at} onChange={e => setEditing({...editing, ends_at: e.target.value})} className="px-3 py-2 border border-stone-300"/>
      <input type="number" placeholder="Capacity" value={editing.capacity} onChange={e => setEditing({...editing, capacity: Number(e.target.value)})} className="px-3 py-2 border border-stone-300"/>
      <textarea placeholder="Description" rows={6} value={editing.description} onChange={e => setEditing({...editing, description: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300"/>
    </div>
  );
}

function RsvpList({ list }) {
  return (
    <>
      <div className="text-sm text-stone-600 mb-4">{list.length} RSVP{list.length !== 1 && "s"}</div>
      <div className="space-y-2">
        {list.map(r => (
          <div key={r.id} className="border-b border-stone-100 py-2 text-sm">
            <div className="font-medium">{r.contact?.name}</div>
            <div className="text-stone-500 text-xs">{r.contact?.email} &middot; guests: {r.guests}</div>
          </div>
        ))}
        {list.length === 0 && <div className="text-stone-500 text-sm">No RSVPs yet.</div>}
      </div>
    </>
  );
}

export default function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [rsvps, setRsvps] = useState(null);
  const load = useCallback(() => adminEvents().then(setItems), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (items.find(i => i.id === editing.id)) {
        await adminUpdateEvent(editing.id, editing); toast.success("Updated");
      } else {
        await adminCreateEvent(editing); toast.success("Created");
      }
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await adminDeleteEvent(id); load();
  };
  const openRsvps = async (e) => setRsvps({ event: e, list: await adminEventRsvps(e.id) });

  return (
    <ResourceList
      eyebrow="Events"
      title="Events."
      testId="admin-events"
      newLabel="New Event"
      onNew={() => setEditing({ ...EMPTY })}
    >
      <div className="grid gap-3">
        {items.map(e => <EventRow key={e.id} e={e} onEdit={setEditing} onDelete={remove} onRsvps={openRsvps}/>)}
      </div>

      <ResourceDrawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit event" : "New event"}
        size="lg"
        onSave={save}
      >
        {editing && <EventForm editing={editing} setEditing={setEditing}/>}
      </ResourceDrawer>

      <ResourceDrawer
        open={!!rsvps}
        onClose={() => setRsvps(null)}
        title={rsvps?.event.title}
        size="md"
      >
        {rsvps && <RsvpList list={rsvps.list}/>}
      </ResourceDrawer>
    </ResourceList>
  );
}
